import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardRow, GridRow, LeadRow, AttrColumn, EditShape } from './rows';
import { useListFetch } from './useListFetch';
import { ListStateShell } from './ListStateShell';
import { ValueSheet, ValueSheetOption } from './ValueSheet';
import { UndoToast } from './UndoToast';

/**
 * LeadsGrid (§5) — the P2 visual grid. Responsive on Tailwind DEFAULTS only (erratum E-1): card list <768,
 * 2-col cards md:(768), TABLE lg:(1024). One data source (/api/customers → getRolodex, now carrying
 * clientStatus/qualificationLevel), rendered by BOTH shapes so AC-P2.4 holds (same data + colours). Edits
 * are OPTIMISTIC → PUT /api/customers/:id/status → UndoToast; failure reverts + errors. The `columns` list
 * is a PROP with a P2 default; P3's saved views will drive it.
 */
// Column labels are CHROME, so they carry keys and the render resolves them. (The VALUES in these columns
// are tenant-authored content and are never translated.)
const DEFAULT_COLUMNS: AttrColumn[] = [
  { key: 'clientStatus', labelKey: 'leadsGrid.columns.clientStatus' },
  { key: 'qualificationLevel', labelKey: 'leadsGrid.columns.qualification' },
];

export const LeadsGrid: React.FC<{
  columns?: AttrColumn[];
  selectedId?: string | null;
  onOpenPerson: (lead: LeadRow) => void;
  onRemove?: (leadId: string) => void;
  /** B5 — fired after a convert/un-convert LANDS, so the host can bump the Contacts list's
   *  refreshKey: the person just moved lists, and the other page must learn it without a remount
   *  (the D-L4 class). */
  onConverted?: () => void;
  /** D-L4 — this grid had NO refetch signal at all: a lead created or converted elsewhere in the
   *  console never appeared until a full reload. Threaded for parity with every other grid. */
  refreshKey?: number;
}> = ({ columns = DEFAULT_COLUMNS, selectedId, onOpenPerson, onRemove, onConverted, refreshKey }) => {
  const { t } = useTranslation('common');
  const [editing, setEditing] = useState<{ lead: LeadRow; col: AttrColumn; anchor: HTMLElement; shape: EditShape } | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'undo' | 'error'; onUndo?: () => void } | null>(null);
  // Render ONE tier at a time (lg: = table, else cards) rather than CSS-toggling both into the DOM — a single
  // list is what the console/specs expect (a CSS-hidden duplicate row would double every data-testid).
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // X-1 via useListFetch (P38 surfaces 3 of 10 — this grid and ContactsGrid were one copied line).
  // getRolodex returns the lead objects; map to the row shape (id=display token, leadId=real id).
  // NAME falls back to the row-id token (as the old card-list did — phone/email live in the contact
  // line, NOT the name), so token-keyed leads stay findable/removable, which ratified specs rely on.
  const list = useListFetch<LeadRow[]>('/api/customers', {
    refreshKey,
    map: (rows) => (rows as any[]).map((l) => ({
      id: l.id, leadId: l.leadId ?? l.id, name: l.name || l.firstName || (l.id ? String(l.id).slice(0, 8) : '—'),
      phone: l.phone, email: l.email, clientStatus: l.clientStatus ?? null, qualificationLevel: l.qualificationLevel ?? null,
      tags: Array.isArray(l.tags) ? l.tags : [], lastActivityAt: l.lastActivityAt ?? l.lastConversationAt, conversationsCount: l.conversationsCount,
    })),
  });
  const leads = list.data ?? [];
  // Optimistic edits/removes mutate the fetched rows in place (the hook owns where rows COME from).
  const setLeads = (fn: (ls: LeadRow[]) => LeadRow[]) => list.setData((d) => fn(d ?? []));

  // Existing distinct values per column → the ValueSheet's option set (tenant builds their palette over time).
  const optionsFor = (key: string): ValueSheetOption[] => {
    const seen = new Set<string>();
    for (const l of leads) { const v = (l as any)[key]; if (v) seen.add(String(v)); }
    return [...seen].map((v) => ({ value: v, label: v }));
  };

  const applyEdit = async (lead: LeadRow, col: AttrColumn, value: string | null) => {
    const prev = ((lead as any)[col.key] ?? null) as string | null;
    setLeads((ls) => ls.map((l) => l.leadId === lead.leadId ? { ...l, [col.key]: value } : l)); // optimistic
    setEditing(null);
    try {
      const res = await fetch(`/api/customers/${lead.id}/status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [col.key]: value }) });
      if (!res.ok) throw new Error(String(res.status));
      setToast({ message: t('leadsGrid.toast.updated', { field: t(col.labelKey) }), variant: 'undo', onUndo: () => { setToast(null); void applyEdit(lead, col, prev); } });
    } catch {
      setLeads((ls) => ls.map((l) => l.leadId === lead.leadId ? { ...l, [col.key]: prev } : l)); // revert
      setToast({ message: t('leadsGrid.toast.failed', { field: t(col.labelKey) }), variant: 'error' });
    }
  };

  const onEditAttr = (lead: LeadRow, col: AttrColumn, anchor: HTMLElement, shape: EditShape) => setEditing({ lead, col, anchor, shape });
  // Removing un-lists the lead — drop it from the local view immediately (the console's onRemove persists it).
  const handleRemove = onRemove ? (leadId: string) => { setLeads((ls) => ls.filter((l) => l.leadId !== leadId)); onRemove(leadId); } : undefined;

  /** B5 (D3/P32) — convert: optimistic removal (the row LEAVES this list), then the durable write;
   *  the toast names the DESTINATION ("moved to Contacts" — a record that vanishes with no
   *  forwarding address reads as a delete) and carries a working Undo. Undo un-converts and
   *  refetches: position restoration is the SERVER's ordering guarantee (convert never touches
   *  lastActivityAt), never a client-side splice. Failure refetches truth + error toast (A3). */
  const handleConvert = async (leadId: string) => {
    setLeads((ls) => ls.filter((l) => l.leadId !== leadId)); // optimistic — the row relocates
    const res = await fetch(`/api/customers/${leadId}/convert`, { method: 'POST' }).catch(() => null);
    if (!res?.ok) {
      list.retry(); // restore truth — the row never left
      setToast({ message: t('leadsGrid.toast.convertFailed'), variant: 'error' });
      return;
    }
    onConverted?.();
    setToast({
      message: t('leadsGrid.toast.converted'), variant: 'undo',
      onUndo: () => {
        setToast(null);
        void fetch(`/api/customers/${leadId}/unconvert`, { method: 'POST' }).then((r) => {
          if (!r.ok) { setToast({ message: t('leadsGrid.toast.convertFailed'), variant: 'error' }); return; }
          list.retry(); // the row returns at its ORIGINAL position (server ordering, untouched clocks)
          onConverted?.();
        }).catch(() => setToast({ message: t('leadsGrid.toast.convertFailed'), variant: 'error' }));
      },
    });
  };
  const headerCols = useMemo(() => columns, [columns]);

  return (
    <div className="relative w-full">
      <ListStateShell status={list.status} testIdBase="leads-grid" onRetry={list.retry}>
      {!isDesktop ? (
      /* Card list — 1-col <768, 2-col md:. Rendered ONLY when not desktop (single list, no duplicate DOM). */
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2">
        {leads.map((l) => <CardRow key={l.leadId} lead={l} columns={columns} selected={l.id === selectedId} onOpenPerson={onOpenPerson} onEditAttr={onEditAttr} onRemove={handleRemove} onConvert={handleConvert} />)}
        {leads.length === 0 && <div className="col-span-full text-center text-sm text-text-muted py-10">{t('leadsGrid.empty')}</div>}
      </div>
      ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full text-start border-collapse">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-text-muted border-b border-border-neutral">
              <th className="sticky z-10 bg-surface p-4 text-start [inset-inline-start:0]">{t('leadsGrid.columns.name')}</th>
              <th className="p-4 text-start">{t('leadsGrid.columns.contact')}</th>
              {headerCols.map((c) => <th key={c.key} className="p-4 text-start">{t(c.labelKey)}</th>)}
              <th className="p-4 text-start">{t('leadsGrid.columns.tags')}</th>
              <th className="p-4 text-start">{t('leadsGrid.columns.lastActivity')}</th>
              <th className="p-4 text-start"><span className="sr-only">{t('leadsGrid.columns.actions')}</span></th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => <GridRow key={l.leadId} lead={l} columns={columns} selected={l.id === selectedId} onOpenPerson={onOpenPerson} onEditAttr={onEditAttr} onRemove={handleRemove} onConvert={handleConvert} />)}
          </tbody>
        </table>
        {leads.length === 0 && <div className="text-center text-sm text-text-muted py-10">{t('leadsGrid.empty')}</div>}
      </div>
      )}
      </ListStateShell>

      {editing && (
        <ValueSheet
          shape={editing.shape}
          title={`${t(editing.col.labelKey)} — ${editing.lead.name}`}
          options={optionsFor(editing.col.key)}
          current={((editing.lead as any)[editing.col.key] ?? null) as string | null}
          anchor={editing.anchor}
          allowCreate
          onSelect={(v) => applyEdit(editing.lead, editing.col, v)}
          onClose={() => setEditing(null)}
        />
      )}
      {toast && <UndoToast message={toast.message} variant={toast.variant} onUndo={toast.onUndo} onExpire={() => setToast(null)} />}
    </div>
  );
};
